import { useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FieldError, useForm } from "react-hook-form";

import ValidationSchema from "./validation";

import { useResetInstitutionPassword } from "@/api/hooks/institution";
import FormPassword from "@/components/kit/FormGenerator/FormPassword";

export interface IResetPasswordData {
    password: string;
    confirmPassword: string;
}

const defaultValues: IResetPasswordData = {
    password: "",
    confirmPassword: "",
};

export default function ResetPasswordDialog({
    open,
    institutionId,
    institutionName,
    onClose,
}: {
    open: boolean,
    institutionId: number | null,
    institutionName?: string,
    onClose: () => void,
}) {
    const { mutate: resetPassword, isPending } = useResetInstitutionPassword();

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<IResetPasswordData>({
        defaultValues,
        mode: "all",
        resolver: yupResolver(ValidationSchema),
    });

    useEffect(() => {
        if (open) reset(defaultValues);
    }, [open, reset]);

    const handleClose = () => {
        if (isPending) return;

        onClose();
    };

    const onSubmit = (data: IResetPasswordData) => {
        if (!institutionId) return;

        resetPassword(
            { id: institutionId, body: { password: data.password, confirmPassword: data.confirmPassword } },
            { onSuccess: () => onClose() }
        );
    };

    return (
        <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary" variant="body2">
                        {institutionName
                            ? `Set a new password for "${institutionName}".`
                            : "Set a new password for this institution."}
                    </Typography>
                    <Controller
                        key="password"
                        control={control}
                        name="password"
                        render={({ field }) => (
                            <FormPassword
                                className="password field"
                                errorMessage={errors["password"] as FieldError}
                                field={{ ...field }}
                                label="New Password"
                                placeholder="At least 8 characters"
                                props={{ required: true }}
                            />
                        )}
                    />
                    <Controller
                        key="confirmPassword"
                        control={control}
                        name="confirmPassword"
                        render={({ field }) => (
                            <FormPassword
                                className="confirmPassword field"
                                errorMessage={errors["confirmPassword"] as FieldError}
                                field={{ ...field }}
                                label="Confirm Password"
                                placeholder="Re-enter the new password"
                                props={{ required: true }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button disabled={isPending} onClick={handleClose}>
                        Cancel
                    </Button>
                    <LoadingButton disabled={isPending} loading={isPending} type="submit" variant="contained">
                        Reset Password
                    </LoadingButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}
